/**
 * Challenge evaluation engine for the Interactive Strategy Canvas.
 *
 * Pure functions that evaluate ChallengeRules against canvas section stats
 * and resolve template placeholders. No framework dependencies.
 */

import type { ChallengeRule } from './templates.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SectionStats {
  count: number
  hasNotes: boolean
}

export interface Challenge {
  id: string
  severity: 'critical' | 'strategic' | 'opportunity'
  title: string
  description: string
  affectedSection: string
  type: ChallengeRule['type']
}

export interface SectionItem {
  label: string
}

// ---------------------------------------------------------------------------
// Placeholder resolution
// ---------------------------------------------------------------------------

/**
 * Resolves {ref:sectionId} placeholders by inserting entity labels.
 * - 0 items → "your customers" (graceful fallback)
 * - 1-3 items → "label1, label2, label3"
 * - 4+ items → "label1, label2 and N more"
 */
export function resolveRefPlaceholders(
  template: string,
  sectionItems: Record<string, SectionItem[]>,
): string {
  return template.replace(/\{ref:([^}]+)\}/g, (_, sectionId: string) => {
    const items = sectionItems[sectionId] || []
    if (items.length === 0) return 'your customers'
    if (items.length <= 3) return items.map((i) => i.label).join(', ')
    return items.slice(0, 2).map((i) => i.label).join(', ') + ` and ${items.length - 2} more`
  })
}

/**
 * Resolves {count:sectionId} placeholders with numeric counts.
 */
export function resolveCountPlaceholders(
  template: string,
  stats: Record<string, SectionStats>,
): string {
  return template.replace(/\{count:([^}]+)\}/g, (_, sectionId: string) => {
    return String(stats[sectionId]?.count || 0)
  })
}

/**
 * Resolves all placeholders ({ref:...} and {count:...}) in a template string.
 */
export function resolvePlaceholders(
  template: string,
  stats: Record<string, SectionStats>,
  sectionItems: Record<string, SectionItem[]>,
): string {
  let result = resolveCountPlaceholders(template, stats)
  result = resolveRefPlaceholders(result, sectionItems)
  return result
}

// ---------------------------------------------------------------------------
// Rule evaluation
// ---------------------------------------------------------------------------

/**
 * Built-in condition evaluators for each challenge rule type.
 * These check section stats to determine if a challenge should fire.
 */
function evaluateRuleCondition(
  rule: ChallengeRule,
  stats: Record<string, SectionStats>,
): boolean {
  const affected = stats[rule.affectedSection]

  switch (rule.type) {
    case 'empty_section':
      // Fire if the affected section is empty AND at least one related section has content
      return (affected?.count || 0) === 0

    case 'imbalance': {
      // Fire if affected section has < 3 items while other sections are larger
      const affectedCount = affected?.count || 0
      if (affectedCount >= 3) return false
      const otherCounts = Object.entries(stats)
        .filter(([id]) => id !== rule.affectedSection)
        .map(([, s]) => s.count)
      return otherCounts.some((c) => c > 5)
    }

    case 'strategic_focus':
      // Fire if affected section has > 8 entities
      return (affected?.count || 0) > 8

    case 'breadth_no_depth': {
      // Fire if many entities exist but most lack notes
      const count = affected?.count || 0
      return count > 5 && !affected?.hasNotes
    }

    case 'cross_section_coherence':
      // Generic — always fires if defined (enriched by AI)
      return (affected?.count || 0) === 0

    default:
      return false
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

const SEVERITY_ORDER: Record<string, number> = { critical: 0, strategic: 1, opportunity: 2 }

/**
 * Evaluates all challenge rules against the current canvas state.
 * Returns challenges sorted by severity (critical first).
 */
export function evaluateChallenges(
  rules: ChallengeRule[],
  stats: Record<string, SectionStats>,
  sectionItems: Record<string, SectionItem[]>,
): Challenge[] {
  const challenges: Challenge[] = []

  for (const rule of rules) {
    if (evaluateRuleCondition(rule, stats)) {
      challenges.push({
        id: rule.id,
        severity: rule.severity,
        title: rule.title,
        description: resolvePlaceholders(rule.descriptionTemplate, stats, sectionItems),
        affectedSection: rule.affectedSection,
        type: rule.type,
      })
    }
  }

  challenges.sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 2) - (SEVERITY_ORDER[b.severity] ?? 2))
  return challenges
}
